import { useEffect } from 'react'
import { supabase } from '../lib/supabase'

// ─── Theme definitions ────────────────────────────────────────────────────────

export const THEMES: Record<string, Record<string, string>> = {
  warm: {
    '--color-background':     '#FAF8F4',
    '--color-surface':        '#F2EDE6',
    '--color-green':          '#6B9E78',
    '--color-green-light':    '#E8F2EA',
    '--color-text-primary':   '#2C2A26',
    '--color-text-secondary': '#7A7468',
    '--color-border':         '#E8E2DC',
  },
  ocean: {
    '--color-background':     '#F0F7FF',
    '--color-surface':        '#E1EFFF',
    '--color-green':          '#2E86AB',
    '--color-green-light':    '#D6EEFF',
    '--color-text-primary':   '#1A2B3C',
    '--color-text-secondary': '#4A6A8A',
    '--color-border':         '#C5DEFF',
  },
  sunset: {
    '--color-background':     '#FFF8F0',
    '--color-surface':        '#FFE8D6',
    '--color-green':          '#E07A5F',
    '--color-green-light':    '#FFE0D6',
    '--color-text-primary':   '#2C1810',
    '--color-text-secondary': '#7A4A3A',
    '--color-border':         '#FFD5C0',
  },
  forest: {
    '--color-background':     '#F2F7F2',
    '--color-surface':        '#E3EDE3',
    '--color-green':          '#4A7C59',
    '--color-green-light':    '#D6EDD6',
    '--color-text-primary':   '#1A2C1A',
    '--color-text-secondary': '#4A6A4A',
    '--color-border':         '#C5DCC5',
  },
  lavender: {
    '--color-background':     '#F8F4FF',
    '--color-surface':        '#EDE8FF',
    '--color-green':          '#7B68EE',
    '--color-green-light':    '#E0D8FF',
    '--color-text-primary':   '#1A1040',
    '--color-text-secondary': '#5A4A8A',
    '--color-border':         '#D5C8FF',
  },
  pink: {
    '--color-background':     '#FFF5F7',
    '--color-surface':        '#FFE8ED',
    '--color-green':          '#E8698A',
    '--color-green-light':    '#FFD6E0',
    '--color-text-primary':   '#2C1A1F',
    '--color-text-secondary': '#7A4A55',
    '--color-border':         '#FFCCD5',
  },
}

// ─── Apply helpers ────────────────────────────────────────────────────────────

export function applyTheme(themeName: string) {
  const theme = THEMES[themeName] ?? THEMES.warm
  const root  = document.documentElement

  // Set every CSS variable so Tailwind utilities like bg-background update
  Object.entries(theme).forEach(([key, value]) => root.style.setProperty(key, value))

  // Also stamp body directly — catches any elements that don't use CSS vars
  document.body.style.backgroundColor = theme['--color-background']
  document.body.style.color           = theme['--color-text-primary']
}

export function applyFontSize(size: string) {
  const sizes: Record<string, string> = { small: '14px', normal: '16px', large: '18px' }
  document.documentElement.style.setProperty('--font-size-base', sizes[size] ?? '16px')
}

export function applyCompactMode(compact: boolean) {
  document.documentElement.classList.toggle('compact-mode', compact)
}

// ─── Hook — loads saved prefs on mount and applies them ───────────────────────

export interface UserPreferences {
  theme:       string
  fontSize:    string
  compactMode: boolean
}

export function useTheme() {
  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const { data: userData } = await supabase
        .from('users')
        .select('preferences')
        .eq('id', session.user.id)
        .single()

      if (userData?.preferences) {
        const { theme, fontSize, compactMode } = userData.preferences as Partial<UserPreferences>
        if (theme)                    applyTheme(theme)
        if (fontSize)                 applyFontSize(fontSize)
        if (compactMode !== undefined) applyCompactMode(compactMode)
      }
    }

    load()
  }, []) // runs once on mount — re-runs when component remounts (page navigation)
}
